import React, { useState } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { getTenantColor } from '../utils/tenantColors';
import { ChevronDownIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import './TenantSelector.css';

const TenantSelector: React.FC = () => {
  const { currentTenant, availableTenants, switchTenant, isLoading } = useTenant();
  const [isOpen, setIsOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="tenant-selector-loading">
        Loading tenants...
      </div>
    );
  }

  if (availableTenants.length === 0) {
    return null;
  }

  // Single tenant - show as static badge
  if (availableTenants.length === 1) {
    const tenantColor = currentTenant ? getTenantColor(currentTenant.id) : undefined;
    return (
      <div className="tenant-selector">
        <div
          className="tenant-badge"
          style={{ borderLeftColor: tenantColor }}
        >
          <BuildingOfficeIcon className="tenant-icon" />
          <span>{currentTenant?.name}</span>
        </div>
      </div>
    );
  }

  // Multiple tenants - show dropdown
  const currentTenantColor = currentTenant ? getTenantColor(currentTenant.id) : undefined;

  return (
    <div className="tenant-selector">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="tenant-dropdown-button"
        style={{ borderLeftColor: currentTenantColor }}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Select tenant"
      >
        <div className="tenant-dropdown-button-content">
          <BuildingOfficeIcon className="tenant-icon" aria-hidden="true" />
          <span>{currentTenant?.name}</span>
        </div>
        <ChevronDownIcon
          className={`tenant-dropdown-icon ${isOpen ? 'open' : ''}`}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <>
          {/* Backdrop to close dropdown */}
          <div
            className="tenant-dropdown-backdrop"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />

          {/* Dropdown menu */}
          <div
            className="tenant-dropdown-menu"
            role="listbox"
            aria-label="Available tenants"
          >
            {[...availableTenants]
              .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
              .map((tenant) => {
                const isSelected = currentTenant?.id === tenant.id;
                const tenantColor = getTenantColor(tenant.id);

                return (
                  <button
                    key={tenant.id}
                    onClick={() => {
                      switchTenant(tenant.id);
                      setIsOpen(false);
                    }}
                    className={`tenant-dropdown-item ${isSelected ? 'selected' : ''}`}
                    style={{ borderLeftColor: tenantColor }}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <BuildingOfficeIcon className="tenant-icon" aria-hidden="true" />
                    <span className="tenant-dropdown-item-name">
                      {tenant.name}
                    </span>
                    {isSelected && (
                      <span className="tenant-dropdown-checkmark">✓</span>
                    )}
                  </button>
                );
              })}
          </div>
        </>
      )}
    </div>
  );
};

export default TenantSelector;
